import config from '@config';
import xObject from './xObject';

declare let window: any;

export enum TrackerEcommerceEventType
{
    ViewProducts,
    AddToWishList,
    AddToCart,
    RemoveFromCart,
    ViewCart,
    InitiateCheckout,
    AddPaymentInfo,
    Purchase,
    PaymentFailed,
    ChangePaymentMethod,
    Refund,
    ReserveProduct
}

/** Tracking info for a product */
export interface TrackerProduct
{
    id: number | string;
    name: string;
    price?: number;
    quantity?: number;
    category?: string;
    brand?: string;
    variant?: string,
    discount?: number;
    coupon?: number;
    url?: string;
    imageUrl?: string;
}

/** Tracking data for an eCommerce event. */
export interface TrackerEcommerceData
{
    type: TrackerEcommerceEventType,
    value: number;
    currency: string;
    products: TrackerProduct[];
    couponCode?: string;
    transactionId?: number | string;
    shippingCost?: number;
    tax?: number;
    paymentType?: string;
}

/** Extra user info for  enhanced Google Ads and/or Facebook Conversions API. */
export interface TrackerExtraUserData 
{
    emails?: string[],
    phoneNumbers?: string[],
    firstName?: string,
    lastName?: string,
    postalCode?: string, 
    streetAddress?: string,
    countryCode?: string,
}

/**
 * Track user interactions with the app.
 */
export class Tracker extends xObject
{
    /** The singleton instance. */
    protected static _instance?: Tracker;
    /** Does the Tracker load the tracking scripts or are they already loaded with the classic <script> tags? */
    handleScriptLoading: boolean = true;
    /** Have the scripts started loading? */
    loadingScriptsStarted = false;
    /** Is tracking enabled? */
    enabled = true;
    /** Track with Google Analytics/ Google Ads? */
    trackWithGoogle = false;
    /** Track with Facebook? */
    trackWithFacebook = false;
    /** Track with Facebook Conversions API? */
    trackWithFacebookConversionsApi = false;
    /** Track the page view when the scripts load? */
    trackPageViewAtScriptLoad = true;
    /** Lazy load the tracking scripts? Eg: on use interaction or after a certain time passes after the page load. */
    lazyLoadScripts = false;
    /** The Google gtag object. */
    gtag?: Gtag.Gtag;
    /** The Facebook fbq object. */
    fbq?: facebook.Pixel.Event;

    //#region Singleton
    protected constructor()
    {
        super();
        
        this.init();
    }

    static get instance(): Tracker
    {
        if (this._instance === undefined) this._instance = this.createInstance();

        return this._instance;
    }

    protected static createInstance(): Tracker
    {
        return new Tracker();
    }
    //#endregion

    /**
     * Load the tracking scripts.
     */
    protected async init()
    {
        if (this.handleScriptLoading)
        {// scripts are loaded by the Tracker
            if (this.lazyLoadScripts)
            {// Load the tracking scripts when a user interects with the page or a certain amount of time expires after the page load
                addEventListener('scroll', () => this.loadScripts());
                addEventListener('mousemove', () => this.loadScripts());
                addEventListener('touchstart', () => this.loadScripts());
                addEventListener('touchmove', () => this.loadScripts());
                addEventListener('click', () => this.loadScripts());

                let timeout = 5000;
                setTimeout(() => this.loadScripts(), timeout);
            }
            else
            {// Load the tracking scripts immediately
                this.loadScripts();
            }
        }
        else
        {// scripts are already loaded with the classic <script> tags
            addEventListener('load', () => {
                this.gtag = window.gtag;
                this.fbq = window.fbq;
                this.fire('loaded');
            });
        }
    }

    /**
     * Load the tracking scripts. Fire the 'loaded' event when done.
     */
    private async loadScripts()
    {
        if (this.loadingScriptsStarted) return;
        this.loadingScriptsStarted = true;

        console.log('Tracker: loading tracking scripts...');

        await Promise.all([this.loadFacebookScript(), this.loadGoogleScript()]);
        this.fire('loaded');

        console.log('Tracker: tracking scripts loaded.');
    }

    /**
    * Load the Google Analytics tracking code.
    * https://developers.google.com/analytics/devguides/collection/gtagjs
    * https://developers.google.com/tag-platform/tag-manager/web/datalayer
    * https://support.google.com/google-ads/answer/7548399
    * https://support.google.com/google-ads/answer/12785474?visit_id=638155969923163305-1440919079&rd=1#
    */
    private loadGoogleScript()
    {
        return new Promise<void>((resolve) =>
        {
            console.log('Tracker: loading Google Analytics...');

            // Load the tracking script
            let script = document.createElement('script');
            script.src = 'https://www.googletagmanager.com/gtag/js?id=' + config.googleAnalyticsId;
            script.async = true;
            script.onload = () => {
                // Initialize the data layer (events in the data layer will be sent to Google as soon as the gtag script has been loaded)
                window.dataLayer = window.dataLayer || [];
                this.gtag = function () { window.dataLayer.push(arguments); }
                window.gtag = this.gtag;
                this.gtag('js', new Date());

                // Conversion linker
                this.gtag('config', config.googleAnalyticsId, {
                    'linker': {
                        'domains': ['bbmoto.ro']
                    }
                });
                
                // Add Google Analytics tag (GA4)
                let gtagConfig: { [key: string]: any } = {
                    send_page_view: this.trackPageViewAtScriptLoad 
                };
                if (config.googleAnalyticsServerContainerUrl != null) gtagConfig['server_container_url'] = config.googleAnalyticsServerContainerUrl; // if server side tracking is enabled
                this.gtag('config', config.googleAnalyticsId, gtagConfig);

                // Add legacy Google Universal Analytics tag
                if (config.googleUniversalAnalyticsId != null)
                {
                    this.gtag('config', config.googleUniversalAnalyticsId, { 
                        send_page_view: this.trackPageViewAtScriptLoad 
                    });
                }

                // Add Google Ads tag
                if (config.googleAdsId != null)
                {
                    this.gtag('config', config.googleAdsId, { 
                        send_page_view: this.trackPageViewAtScriptLoad, 
                        allow_enhanced_conversions: true 
                    });
                }

                // Consent
                this.gtag('consent', 'default', {
                    'ad_storage': 'granted', 
                    // @ts-ignore
                    'ad_user_data': 'granted',
                    'ad_personalization': 'granted',
                    'analytics_storage': 'granted',
                    'functionality_storage': 'granted',
                    'personalization_storage': 'granted',
                });

                resolve();
                console.log('Tracker: Google Analytics loaded.');
            };
            document.body.appendChild(script);
        });
    }

    /**
    * Load the Facebook pixel code.
    * https://www.facebook.com/business/help/952192354843755?id=1205376682832142
    */
    private loadFacebookScript()
    {
        console.log('Tracker: loading Facebook pixel...');

        return new Promise<void>((resolve) =>
        {
            // A heavily modified version of the Facebook pixel code
            const f = function (p_window?: any, p_document?: any, p_script?: any, p_src?: any, n?: any, script?: any, onLoaded?: any)
            {
                if (p_window.fbq) return;
                n = p_window.fbq = function ()
                {
                    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
                };
                if (!p_window._fbq) p_window._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [];

                script = p_document.createElement(p_script);
                script.async = true;
                script.src = p_src;
                script.onload = () => {
                    if (onLoaded) onLoaded();
                };
                document.body.appendChild(script);
            };

            f(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', undefined, undefined, () => {
                //fbq('set', 'autoConfig', 'false', config.facebookPixelID); 
                fbq('init', config.facebookPixelId);
                if (this.trackPageViewAtScriptLoad) fbq('track', 'PageView');
                this.fbq = fbq;

                resolve();
                console.log('Tracker: Facebook pixel loaded.');
            });
        });
    }

    /**
    * Log a custom event.
    * https://developers.google.com/analytics/devguides/collection/ga4/events?client_type=gtag
    * https://developers.facebook.com/docs/meta-pixel/implementation/conversion-tracking
    * 
    * @param name 
    * @param parameters 
    * @param extraUserData
    */
    logEvent(name: string, parameters: any = {}, extraUserData?: TrackerExtraUserData)
    {
        // Google
        this.logGoogleEvent(name, parameters);

        // Facebook
        this.logFacebookEvent(name, parameters);

        // Facebook Conversions API
        this.logFacebookConversionsApiEvent(name, window?.location?.href, parameters, extraUserData);

        // TikTok
        this.logTikTokEvent(name, parameters);
    }

    /**
    * Log a page view (Google only).
    * https://developers.google.com/analytics/devguides/collection/gtagjs/pages
    * https://developers.facebook.com/docs/meta-pixel/reference#standard-events
    */
    logPageView(pageTitle: string = document.title, pageUrl: string = location.href, extraUserData?: TrackerExtraUserData)
    {
        // Google
        let parameters: any = {
            page_title: pageTitle,
            page_location: pageUrl,
        };
        this.logGoogleEvent('page_view', parameters);

        // Facebook
        parameters = {
            content_name: pageTitle,
        };
        this.logFacebookEvent('ViewContent', parameters);

        // Facebook Conversions API
        this.logFacebookConversionsApiEvent('ViewContent', pageUrl, parameters, extraUserData);

        // TikTok
        this.logTikTokEvent('ViewContent', parameters);
    }

    /**
     * Log an eCommerce related event.
     * 
     * @param data eCommerce data
     * @param extraUserData
     */
    logEcommerceEvent(data: TrackerEcommerceData, extraUserData?: TrackerExtraUserData)
    {
        // Google data (GA4)
        // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
        let googleData = {
            transaction_id: data.transactionId,
            value: data.value,
            currency: data.currency,
            tax: data.tax,
            shipping: data.shippingCost,
            coupon: data.couponCode,
            payment_type: data.paymentType,
            items: data.products.map(product => ({
                id: product.id,
                item_id: product.id,
                item_name: product.name,
                price: product.price,
                quantity: product.quantity,
                item_category: product.category,
                item_brand: product.brand,
                item_variant: product.variant,
                google_business_vertical: 'retail'
            }))
        };

        // Google data (Universal Analytics)
        // https://developers.google.com/analytics/devguides/collection/ua/gtm/enhanced-ecommerce
        // https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce
        let googleDataUniversal = {
            transaction_id: '' + data.transactionId + '',
            affiliation: 'Online Store',
            value: data.value,
            currency: data.currency,
            tax: data.tax,
            shipping: data.shippingCost,
            items: data.products.map(product => ({
                id: '' + product.id + '',
                name: product.name,
                brand: product.brand,
                category: product.category,
                variant: product.variant,
                price: product.price,
                quantity: product.quantity,
            }))
        };

        // Facebook data
        // https://developers.facebook.com/docs/meta-pixel/reference#standard-events
        let facebookData = {
            currency: data.currency,
            value: data.value,
            content_type: 'product',
            contents: data.products.map(product => ({
                id: product.id,
                title: product.name,
                item_price: product.price,
                quantity: product.quantity,
            }))
        };

        // https://support.google.com/google-ads/answer/7305793?hl=en
        let googleAdsData = {
            transaction_id: data.transactionId,
            value: data.value,
            currency: data.currency,
            items: data.products.map(product => ({
                id: product.id,
                google_business_vertical: 'retail'
            }))
        };

        // https://business-api.tiktok.com/portal/docs?id=1739585702922241
        let tikTokData = {
            content_type: 'product',
            contents: data.products.map(product => ({
                content_id: product.id.toString(),
                content_name: product.name,
                quantity: product.quantity,
                price: product.price,
            })),
            value: data.value,
            currency: data.currency,
        };

        // Event name
        let googleEventName: string = '';
        let googleUniversalAnalyticsEventName: string = '';
        let facebookEventName: string = '';
        let tikTokEventName: string = '';
        switch (data.type)
        {
            case TrackerEcommerceEventType.ViewProducts:
                {
                    googleEventName = 'view_item';
                    googleUniversalAnalyticsEventName = 'view_item';
                    facebookEventName = 'ViewContent';
                    tikTokEventName = 'ViewContent';
                }
                break;
            case TrackerEcommerceEventType.AddToWishList:
                {
                    googleEventName = 'add_to_wishlist';
                    googleUniversalAnalyticsEventName = 'add_to_wishlist';
                    facebookEventName = 'AddToWishlist';
                    tikTokEventName = 'AddToWishlist';
                }
                break;
            case TrackerEcommerceEventType.AddToCart:
                {
                    googleEventName = 'add_to_cart';
                    googleUniversalAnalyticsEventName = 'add_to_cart';
                    facebookEventName = 'AddToCart';
                    tikTokEventName = 'AddToCart';
                }
                break;
            case TrackerEcommerceEventType.RemoveFromCart:
                {
                    googleEventName = 'remove_from_cart';
                    googleUniversalAnalyticsEventName = 'remove_from_cart';
                    facebookEventName = 'RemoveFromCart';
                    tikTokEventName = 'RemoveFromCart';
                }
                break;
            case TrackerEcommerceEventType.ViewCart:
                {
                    googleEventName = 'view_cart';
                    googleUniversalAnalyticsEventName = 'view_cart';
                    facebookEventName = 'ViewCart';
                    tikTokEventName = 'ViewCart';
                }
                break;
            case TrackerEcommerceEventType.InitiateCheckout:
                {
                    googleEventName = 'begin_checkout';
                    googleUniversalAnalyticsEventName = 'begin_checkout';
                    facebookEventName = 'InitiateCheckout';
                    tikTokEventName = 'InitiateCheckout';
                }
                break;
            case TrackerEcommerceEventType.AddPaymentInfo:
                {
                    googleEventName = 'add_payment_info';
                    googleUniversalAnalyticsEventName = 'add_payment_info';
                    facebookEventName = 'AddPaymentInfo';
                    tikTokEventName = 'AddPaymentInfo';
                }
                break;
            case TrackerEcommerceEventType.Purchase:
                {
                    googleEventName = 'purchase';
                    googleUniversalAnalyticsEventName = 'purchase';
                    facebookEventName = 'Purchase';
                    tikTokEventName = 'PlaceAnOrder';
                }
                break;
            case TrackerEcommerceEventType.PaymentFailed:
                {
                    googleEventName = 'payment_failed';
                    googleUniversalAnalyticsEventName = 'payment_failed';
                    facebookEventName = 'PaymentFailed';
                    tikTokEventName = 'PaymentFailed';
                }
                break;
            case TrackerEcommerceEventType.ChangePaymentMethod:
                {
                    googleEventName = 'change_payment_method';
                    googleUniversalAnalyticsEventName = 'change_payment_method';
                    facebookEventName = 'ChangePaymentMethod';
                    tikTokEventName = 'ChangePaymentMethod';
                }
                break;
            case TrackerEcommerceEventType.Refund:
                {
                    googleEventName = 'refund';
                    googleUniversalAnalyticsEventName = 'refund';
                    facebookEventName = 'Refund';
                    tikTokEventName = 'Refund';
                }
                break;
        }

        // Log GA4 event
        this.logGoogleEvent(googleEventName, googleData, config.googleAnalyticsId);
        
        // Log Universal Analytics event
        if (config.googleUniversalAnalyticsId != null)
        {
            this.logGoogleEvent(googleUniversalAnalyticsEventName, googleDataUniversal, config.googleUniversalAnalyticsId);
        }

        // Log Google Ads event
        if (config.googleAdsId != null)
        {
            this.logGoogleEvent(googleEventName, googleAdsData, config.googleAdsId);
        }

        // Log Facebook event
        this.logFacebookEvent(facebookEventName, facebookData);
        this.logFacebookConversionsApiEvent(facebookEventName, window?.location?.href, facebookData, extraUserData);

        // Log TikTok event
        this.logTikTokEvent(tikTokEventName, tikTokData);
    }

    /**
    * Log a Google event with gtag.js.
    * https://developers.google.com/analytics/devguides/collection/gtagjs/events
    * https://developers.google.com/analytics/devguides/collection/ga4/events?client_type=gtag
    * 
    * @param name Event name. 
    * @param parameters Event parameters.
    */
    logGoogleEvent(name: string, parameters: any = {}, sendTo?: string)
    {
        if (!this.enabled || !this.trackWithGoogle || this.gtag === undefined) return;
        let sendToText = sendTo !== undefined ? ` (send to: '${sendTo}').` : '';
        console.log(`Tracker: log Google event '${name}'.${sendToText} Parameters:`);
        console.log(parameters);

        if (parameters == null) parameters = {};
        parameters.version = config.version;
        if (sendTo !== undefined) parameters.send_to = sendTo;

        this.gtag('event', name, parameters);
    }

    /**
     * Log a Google Ads conversion event.
     * https://support.google.com/google-ads/answer/7548399 (Event snippet)
     * https://support.google.com/google-ads/answer/12785474?visit_id=638155969923163305-1440919079&rd=1# (Enhanced conversions)
     * https://www.semetis.com/en/resources/articles/how-to-debug-google-enhanced-conversions-implementation
     * https://support.google.com/google-ads/answer/11956168#zippy=%2Csetup-has-incorrect-data-formatting
     * 
     * @param conversionLabel The conversion label
     * @param parameters The conversion parameters.
     */
    logGoogleAdsEvent(conversionLabel: string, parameters: any = {}, extraUserData?: TrackerExtraUserData)
    {
        if (!this.enabled || !this.trackWithGoogle || this.gtag === undefined) return;

        console.log('Tracker: log Google Ads event ' + conversionLabel + '. Parameters:');
        console.log(parameters);
        console.log('Extra user data:');
        console.log(extraUserData);

        if (parameters == null) parameters = {};
        parameters.version = config.version;

        // Google Ads enhanced conversion data
        if (extraUserData !== undefined && this.gtag !== undefined)
        {
            let user_data = {
                email: extraUserData.emails !== undefined && extraUserData.emails.length > 0 ? extraUserData.emails[0] : undefined,
                phone_number: extraUserData.phoneNumbers !== undefined && extraUserData.phoneNumbers.length > 0 ? extraUserData.phoneNumbers[0] : undefined,
                address: {
                    first_name: extraUserData.firstName,
                    last_name: extraUserData.lastName,
                    postal_code: extraUserData.postalCode,
                    street: extraUserData.streetAddress,
                    country: extraUserData.countryCode
                }
            };

            console.log('user_data:');
            console.log(user_data);
            
            this.gtag('set', 'user_data', user_data);
        }

        // Log the event
        this.logGoogleEvent('conversion', {
            send_to: config.googleAdsId + '/' + conversionLabel,
            ...parameters
        });
    }

    /**
    * Log a Facebook pixel event via JS
    * https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking/
    * 
    * @param name Event name. 
    * @param parameters Event parameters.
    */
    logFacebookEvent(name: string, parameters: any = {})
    {
        if (!this.enabled || !this.trackWithFacebook || this.fbq === undefined) return;

        console.log('Tracker: log Facebook event ' + name + '');

        if (parameters == null) parameters = {};
        parameters.version = config.version;

        this.fbq('trackCustom', name, parameters);
    }

    /**
     * Log a TikTok pixel event.
     * https://business-api.tiktok.com/portal/docs?id=1739585702922241
     * 
     * @param name Event name.
     * @param parameters Event parameters.
     */
    logTikTokEvent(name: string, parameters: any = {})
    {
        if (typeof window === 'undefined' || typeof window.ttq === 'undefined' || !this.enabled) return;

        console.log('Tracker: log TikTok event ' + name + '');

        if (parameters == null) parameters = {};
        parameters.version = config.version;

        window.ttq.track(name, parameters);
    }

    /**
     * Log an event with the Facebook Conversion API.
     * 
     * @param name Event name.
     * @param sourceUrl The site URL where the event happened.
     * @param parameters Event parameters.
     */
    private async logFacebookConversionsApiEvent(name: string, sourceUrl: string, parameters: any = {}, extraUserData?: TrackerExtraUserData)
    {
        if (!this.enabled || !this.trackWithFacebookConversionsApi) return;

        console.log('Tracker: log Facebook Conversions API event ' + name + '');

        // dynamic import to avoid loading the whole apollo client in the bundle
        const { client } = await import('@xFrame4/business/GraphQlClient');
        const { gql } = await import('@apollo/client');

        let mutation = `
            mutation TrackWithFacebookConversionsApi($input: FacebookConversionApiInput!) {
                trackWithFacebookConversionsApi(input: $input)
            }
        `;

        // Add the test event code if it is set.
        let facebookCapiParameters = { ...parameters, test_event_code: config.facebookTestEventCode };

        let input = {
            eventName: name,
            eventSourceUrl: sourceUrl,
            parameters: JSON.stringify(facebookCapiParameters),
            userParameters: JSON.stringify({
                emails: extraUserData?.emails ?? [],
                phones: extraUserData?.phoneNumbers ?? [],
                firstName: extraUserData?.firstName,
                lastName: extraUserData?.lastName
            })
        };

        let { data } = await client.mutate({
            mutation: gql(mutation),
            variables: {
                input: input
            }
        });

        return data.trackWithFacebookConversionsApi as boolean;
    }
}

export default Tracker;